// Support ticket #1026: a failed save used to be the end of the clinician's work — leave the page and
// every goal had to be typed again. The care-plan form now auto-saves its unsaved state here, per
// therapy, and restores it when the clinician comes back. sessionStorage, not localStorage: the draft
// lives only for this browser tab's session and is gone when the tab or browser closes, which keeps
// clinical text off durable device storage. It is cleared the moment the server accepts the plan.
export type CarePlanDraftGoal = {
  horizon: string; domain: string; goal_statement: string; measure: string; unit: string; direction: string;
  baseline_value: string; baseline_date: string; target_value: string; target_date: string; status: string;
}

/** `ids` / `waivers` (by goal position): the saved goal each row continues and its not-yet-saved baseline waiver
 *  reason (#14), so a restored draft keeps both. Optional: drafts written before them restore without. */
export type CarePlanDraft = {
  planType: string; goals: CarePlanDraftGoal[]; attested: boolean; savedAt: string
  ids?: (string | null)[]; waivers?: (string | null)[]
}

const KEY = (therapyId: string) => `owlrx.carerx.careplan.draft.${therapyId}`

function storage(): Storage | null {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null } catch { return null }
}

export function readCarePlanDraft(therapyId: string, store: Storage | null = storage()): CarePlanDraft | null {
  try {
    const raw = store?.getItem(KEY(therapyId))
    if (!raw) return null
    const d = JSON.parse(raw) as Partial<CarePlanDraft>
    if (!Array.isArray(d.goals) || typeof d.savedAt !== 'string') return null
    return { planType: d.planType || 'initial', goals: d.goals, attested: !!d.attested, savedAt: d.savedAt,
      ids: Array.isArray(d.ids) ? d.ids : undefined, waivers: Array.isArray(d.waivers) ? d.waivers : undefined }
  } catch { return null }
}

export function writeCarePlanDraft(therapyId: string, draft: Omit<CarePlanDraft, 'savedAt'>, store: Storage | null = storage(), now: Date = new Date()): CarePlanDraft | null {
  if (!store) return null
  const full: CarePlanDraft = { ...draft, savedAt: now.toISOString() }
  try { store.setItem(KEY(therapyId), JSON.stringify(full)); return full } catch { return null }
}

export function clearCarePlanDraft(therapyId: string, store: Storage | null = storage()): void {
  try { store?.removeItem(KEY(therapyId)) } catch { /* nothing to clear */ }
}

const PREFIX = 'owlrx.carerx.careplan.draft.'

/** Identity boundary (sign-in and sign-out): drafts are keyed by therapy, not user, and the tab outlives the login. */
export function clearAllCarePlanDrafts(store: Storage | null = storage()): void {
  try {
    if (!store) return
    const doomed: string[] = []
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i)
      if (k && k.startsWith(PREFIX)) doomed.push(k)
    }
    doomed.forEach(k => store.removeItem(k))
  } catch { /* nothing persisted, nothing to clear */ }
}

/** A draft is worth restoring only when it differs from what the server already holds. */
export function draftDiffers(draft: CarePlanDraft, serverGoals: CarePlanDraftGoal[], serverPlanType: string): boolean {
  return draft.planType !== serverPlanType || JSON.stringify(draft.goals) !== JSON.stringify(serverGoals)
}
